import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete } from '@mui/icons-material';
import useSortTable from '../../../hooks/useSortTable';
import retentionService from '../services/retentionService';
import RetentionLineTable from '../components/RetentionLineTable';
import RetentionLineForm from '../components/RetentionLineForm';
import proceedingService from '../../proceedings/services/proceedingService';
import ProceedingModalForm from '../../proceedings/components/ProceedingModalForm';
import companyService from '../../companies/services/companyService';

const RetentionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [retention, setRetention] = useState(null);
  const [retentionLines, setRetentionLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [proceedings, setProceedings] = useState([]);
  const [openProceedingModal, setOpenProceedingModal] = useState(false);
  const [selectedProceeding, setSelectedProceeding] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [retentions, setRetentions] = useState([]);

  const { sorted: sortedProceedings, sortBy, sortDir, handleSort } = useSortTable(proceedings);

  const SortCell = ({ label, field }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  useEffect(() => {
    loadRetention();
    loadRetentionLines();
    loadProceedings();
    loadCompanies();
  }, [id]);

  const loadProceedings = async () => {
    try {
      const result = await proceedingService.getAll({ retentionId: id, limit: 50 });
      setProceedings(result.data || []);
    } catch (error) {
      console.error('Error loading proceedings:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const result = await companyService.getAll({ limit: 100 });
      setCompanies(result.data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadRetentionsForCompany = async (companyId) => {
    try {
      const result = await retentionService.getAll({ companyId, limit: 100 });
      setRetentions(result.data || []);
    } catch (error) {
      console.error('Error loading retentions:', error);
      setRetentions([]);
    }
  };

  const handleCreateProceeding = () => {
    setSelectedProceeding(null);
    if (retention?.companyId) {
      loadRetentionsForCompany(retention.companyId);
    }
    setOpenProceedingModal(true);
  };

  const handleEditProceeding = (proceeding) => {
    setSelectedProceeding(proceeding);
    setOpenProceedingModal(true);
  };

  const handleDeleteProceeding = async (proceedingId) => {
    if (!window.confirm('¿Eliminar este expediente?')) return;
    try {
      await proceedingService.delete(proceedingId);
      showSnackbar('Expediente eliminado');
      loadProceedings();
    } catch (error) {
      console.error('Error deleting proceeding:', error);
      showSnackbar('Error al eliminar el expediente', 'error');
    }
  };

  const handleSaveProceeding = async (data) => {
    try {
      if (selectedProceeding) {
        await proceedingService.update(selectedProceeding.id, data);
        showSnackbar('Expediente actualizado exitosamente');
      } else {
        await proceedingService.create(data);
        showSnackbar('Expediente creado exitosamente');
      }
      setOpenProceedingModal(false);
      setSelectedProceeding(null);
      loadProceedings();
    } catch (error) {
      console.error('Error saving proceeding:', error);
      throw error;
    }
  };

  const loadRetention = async () => {
    try {
      setLoading(true);
      const data = await retentionService.getById(id);
      setRetention(data.data);
    } catch (error) {
      console.error('Error loading retention:', error);
      showSnackbar('Error al cargar la tabla de retención', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRetentionLines = async () => {
    try {
      const lines = await retentionService.getLines(id);
      setRetentionLines(lines);
    } catch (error) {
      console.error('Error loading retention lines:', error);
      showSnackbar('Error al cargar las líneas de retención', 'error');
    }
  };

  const handleCreateLine = () => {
    setSelectedLine(null);
    setOpenModal(true);
  };

  const handleEditLine = (line) => {
    setSelectedLine(line);
    setOpenModal(true);
  };

  const handleSaveLine = async (data) => {
    try {
      if (selectedLine) {
        await retentionService.updateLine(selectedLine.id, data);
        showSnackbar('Línea actualizada exitosamente');
      } else {
        await retentionService.createLine(id, data);
        showSnackbar('Línea creada exitosamente');
      }
      setOpenModal(false);
      loadRetentionLines();
    } catch (error) {
      console.error('Error saving line:', error);
      showSnackbar(error.response?.data?.error || 'Error al guardar la línea', 'error');
      throw error;
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('¿Está seguro de eliminar esta línea de retención?')) return;

    try {
      await retentionService.deleteLine(lineId);
      showSnackbar('Línea eliminada exitosamente');
      loadRetentionLines();
    } catch (error) {
      console.error('Error deleting line:', error);
      showSnackbar(error.response?.data?.error || 'Error al eliminar la línea', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!retention) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Tabla de retención no encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/retentions')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Tabla de Retención
        </Typography>
      </Box>

      {/* Información de la Retention */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Nombre
            </Typography>
            <Typography variant="h6" gutterBottom>
              {retention.name}
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="text.secondary">
              Código
            </Typography>
            <Typography variant="h6" gutterBottom>
              {retention.code}
            </Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" color="text.secondary">
              Fecha
            </Typography>
            <Typography variant="h6" gutterBottom>
              {new Date(retention.date).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Empresa
            </Typography>
            <Typography variant="body1">
              {retention.company?.name} ({retention.company?.short})
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Área
            </Typography>
            <Typography variant="body1">
              {retention.area?.name} ({retention.area?.code})
            </Typography>
          </Grid>

          {retention.comments && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Comentarios
              </Typography>
              <Typography variant="body1">
                {retention.comments}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Chip
              label={`${retentionLines.length} Líneas de Retención`}
              color="primary"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Sección de Líneas de Retención */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Líneas de Retención
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateLine}
        >
          Crear Línea
        </Button>
      </Box>

      <RetentionLineTable
        lines={retentionLines}
        onEdit={handleEditLine}
        onDelete={handleDeleteLine}
      />

      <Divider sx={{ my: 3 }} />

      {/* Expedientes Asociados */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Expedientes Asociados ({proceedings.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateProceeding}
        >
          Crear Expediente
        </Button>
      </Box>
      <Box sx={{ mb: 3 }}>
        {proceedings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay expedientes asociados a esta tabla de retención.
          </Typography>
        ) : (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortCell label="Nombre" field="name" />
                    <SortCell label="Código" field="code" />
                    <SortCell label="Fecha Inicio" field="startDate" />
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedProceedings.map((p) => (
                    <TableRow
                      key={p.id}
                      hover
                      onClick={() => navigate(`/proceedings/${p.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.code}</TableCell>
                      <TableCell>{p.startDate ? new Date(p.startDate).toLocaleDateString('es-ES', { timeZone: 'UTC' }) : '—'}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => handleEditProceeding(p)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDeleteProceeding(p.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      <RetentionLineForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSaveLine}
        line={selectedLine}
      />

      <ProceedingModalForm
        open={openProceedingModal}
        onClose={() => { setOpenProceedingModal(false); setSelectedProceeding(null); }}
        onSave={handleSaveProceeding}
        proceeding={selectedProceeding || {
          companyId: retention?.companyId || '',
          retentionLine: { retentionId: retention?.id },
        }}
        companies={companies}
        retentions={retentions}
        onCompanyChange={loadRetentionsForCompany}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RetentionDetail;
